import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Personnel } from '../../../../shared/types/game';
import { PersonnelConfig, Competence } from '../../../../shared/types/config';
import { getApiBaseUrl } from '../../../../core/config/api';
import { getServiceIdFromBuildingType } from '../../utils/buildingServiceMapping';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import { updateUser } from '../../../../core/store/slices/userSlice';
import { statuts } from '../../../../config';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import { isTerminalInterventionInstanceStatutRaw } from '../../../../shared/constants/interventionInstanceStatus';
import OperationalPlanningModal, {
  PLANNING_GROUPE_KEYS,
  normalizePlanningGroupeValue,
  type PlanningGroupeKey,
} from '../OperationalPlanningModal';
import AddPersonnelModal from './AddPersonnelModal';
import PersonnelDetailsModal from './PersonnelDetailsModal';
import { usePersonnelTab } from './usePersonnelTab';
import PersonnelCard from './PersonnelCard';

interface BuildingPersonnelTabProps {
  personnel: Personnel[];
  loadingPersonnel: boolean;
  buildingType: string;
  buildingId: number;
  buildingName?: string;
  userId: number;
  onPersonnelAdded?: () => void;
}

const BuildingPersonnelTab: React.FC<BuildingPersonnelTabProps> = ({
  personnel,
  loadingPersonnel,
  buildingType,
  buildingId,
  buildingName = '',
  userId,
  onPersonnelAdded,
}) => {
  const {
    showOperationalPlanningModal,
    setShowOperationalPlanningModal,
    showAddModal,
    setShowAddModal,
    personnelConfigs,
    loadingConfigs,
    searchQuery,
    setSearchQuery,
    isRecruiting,
    recruitError,
    recruitSuccess,
    handleRecruit,
    filteredPersonnel,
    paginatedPersonnel,
    totalPages,
    currentPage,
    setCurrentPage,
    personnelSearchQuery,
    setPersonnelSearchQuery,
    personnelFilterActif,
    setPersonnelFilterActif,
    viewMode,
    setViewMode,
    selectedPersonnel,
    setSelectedPersonnel,
    showPersonnelDetailsModal,
    setShowPersonnelDetailsModal,
    personnelCompetences,
    gradeDefaultCompetences,
    loadingCompetences,
    competenceSearchQuery,
    setCompetenceSearchQuery,
    posteNom,
    statutGeneral,
    statutPlanning,
    derniereIntervention,
    personnelGroupeSelect,
    savingGroupe,
    editingNom,
    setEditingNom,
    nomValue,
    setNomValue,
    prenomValue,
    setPrenomValue,
    savingNom,
    deletingPersonnelId,
    handleOpenPersonnelDetails,
    handleSaveNom,
    handleCancelEditNom,
    handleDeletePersonnel,
    savePersonnelGroupe,
    getStatutGeneralColors,
    getStatutPlanningColors,
    t,
  } = usePersonnelTab({
    personnel,
    buildingType,
    buildingId,
    onPersonnelAdded,
  });


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          Personnel ({filteredPersonnel.length}{filteredPersonnel.length !== personnel.length ? ` / ${personnel.length}` : ''})
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowOperationalPlanningModal(true)}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Planning
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            + Ajouter
          </button>
        </div>
      </div>

      {loadingPersonnel ? (
        <div className="text-center py-12 text-gray-400">
          Chargement du personnel...
        </div>
      ) : personnel.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg mb-2">Aucun personnel assigné</p>
          <p className="text-sm">Cliquez sur "Ajouter" pour assigner du personnel à ce bâtiment</p>
        </div>
      ) : (
        <>
          {/* Barre de recherche et filtres */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Rechercher par nom, prénom ou grade..."
                  value={personnelSearchQuery}
                  onChange={(e) => setPersonnelSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <svg
                  className="absolute left-3 top-2.5 w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <select
                value={personnelFilterActif}
                onChange={(e) => setPersonnelFilterActif(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tous</option>
                <option value="actif">Actifs</option>
                <option value="inactif">Inactifs</option>
              </select>
              <div
                className="flex shrink-0 overflow-hidden rounded-lg border border-gray-600"
                role="group"
                aria-label="Mode d’affichage liste personnel"
              >
                <button
                  type="button"
                  onClick={() => setViewMode('compact')}
                  title="Vue compacte"
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium ${
                    viewMode === 'compact'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" />
                  </svg>
                  <span className="hidden sm:inline">Compacte</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('detailed')}
                  title="Vue détaillée"
                  className={`flex items-center gap-2 border-l border-gray-600 px-3 py-2 text-sm font-medium ${
                    viewMode === 'detailed'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" />
                  </svg>
                  <span className="hidden sm:inline">Détaillée</span>
                </button>
              </div>
            </div>
          </div>

          {/* Liste du personnel avec hauteur fixe et scroll */}
          <div className="max-h-[500px] overflow-y-auto pr-2">
            {filteredPersonnel.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-lg mb-2">Aucun résultat trouvé</p>
                <p className="text-sm">Essayez de modifier vos filtres de recherche</p>
              </div>
            ) : (
              <div className={`grid ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3' : 'grid-cols-1 gap-3'}`}>
                {paginatedPersonnel.map((p) => (
                  <PersonnelCard key={p.id} p={p} compact={viewMode === 'compact'} onOpenDetails={handleOpenPersonnelDetails} />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-700">
              <div className="text-sm text-gray-400">
                Page {currentPage} sur {totalPages} • {filteredPersonnel.length} résultat{filteredPersonnel.length > 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
                >
                  Précédent
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded text-sm ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 hover:bg-gray-600 text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <AddPersonnelModal
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        loadingConfigs={loadingConfigs}
        personnelConfigs={personnelConfigs}
        isRecruiting={isRecruiting}
        recruitError={recruitError}
        recruitSuccess={recruitSuccess}
        handleRecruit={handleRecruit}
      />

      <PersonnelDetailsModal
        showPersonnelDetailsModal={showPersonnelDetailsModal}
        setShowPersonnelDetailsModal={setShowPersonnelDetailsModal}
        selectedPersonnel={selectedPersonnel}
        setSelectedPersonnel={setSelectedPersonnel}
        personnelCompetences={personnelCompetences}
        gradeDefaultCompetences={gradeDefaultCompetences}
        loadingCompetences={loadingCompetences}
        competenceSearchQuery={competenceSearchQuery}
        setCompetenceSearchQuery={setCompetenceSearchQuery}
        posteNom={posteNom}
        statutGeneral={statutGeneral}
        statutPlanning={statutPlanning}
        derniereIntervention={derniereIntervention}
        personnelGroupeSelect={personnelGroupeSelect}
        savingGroupe={savingGroupe}
        editingNom={editingNom}
        setEditingNom={setEditingNom}
        nomValue={nomValue}
        setNomValue={setNomValue}
        prenomValue={prenomValue}
        setPrenomValue={setPrenomValue}
        savingNom={savingNom}
        deletingPersonnelId={deletingPersonnelId}
        buildingType={buildingType}
        buildingName={buildingName}
        showOperationalPlanningModal={showOperationalPlanningModal}
        setShowOperationalPlanningModal={setShowOperationalPlanningModal}
        buildingId={buildingId}
        handleSaveNom={handleSaveNom}
        handleCancelEditNom={handleCancelEditNom}
        handleDeletePersonnel={handleDeletePersonnel}
        savePersonnelGroupe={savePersonnelGroupe}
        getStatutGeneralColors={getStatutGeneralColors}
        getStatutPlanningColors={getStatutPlanningColors}
        t={t}
      />

      <OperationalPlanningModal
        isOpen={showOperationalPlanningModal}
        onClose={() => setShowOperationalPlanningModal(false)}
        buildingId={buildingId}
        buildingName={buildingName || null}
        buildingType={buildingType}
      />
    </div>
  );
};

export default BuildingPersonnelTab;
